import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Action,
  Alert,
  Badge,
  Card,
  Combobox,
  ConfirmModal,
  Modal,
  Page,
  PageStatus,
  RelativeDateTime,
  SimpleSelect,
  TextInput,
  useToast,
} from 'corso-admin-ui';
import {
  commandPlatformApi,
  CORSO_STORE_ORGANIZATION_NAME,
  RoleCode,
  type CommandAdminApi,
} from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { useDebounceValue } from 'usehooks-ts';
import { z } from 'zod';
import api from '~/api';
import { DataListCard, type DataListEntry } from '~/components/DataList';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import { usePathParams } from '~/hooks/usePathParams';
import { roleCodeLabel, roleCodeOptions } from '~/utils/roleCodeLabel';

type Organization = CommandAdminApi.ResponseBody<
  '/organization/:organizationId',
  'get'
>;
type Store = CommandAdminApi.ResponseBody<
  '/organization/:organizationId/store',
  'get'
>[number];
type User = Organization['users'][number];

const { idColumn, nameColumn, shopifyDomainColumn, createdOnColumn } =
  sharedTableDefinitions;

const addStoreSchema =
  commandPlatformApi['/organization/:organizationId/store'].post.request.body;

const addUserSchema =
  commandPlatformApi['/organization/:organizationId/user'].post.request.body;

function useOrganizationAddStore(organizationId?: number) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (values: z.infer<typeof addStoreSchema>) =>
      api.organization(organizationId).addStore(values),
    onSuccess: () => {
      toast.show('Added store');
      return queryClient.invalidateQueries({
        queryKey: ['organization', `${organizationId}`],
      });
    },
    onError: () => toast.show('Failed to add store'),
  });
}

function useOrganizationAddUser(organizationId?: number) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (values: z.infer<typeof addUserSchema>) =>
      api.organization(organizationId).addUser(values),
    onSuccess: () => {
      toast.show('Added user');
      return queryClient.invalidateQueries({
        queryKey: ['organization', `${organizationId}`],
      });
    },
    onError: () => toast.show('Failed to add user'),
  });
}

function useOrganizationRemoveUser(organizationId?: number) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (uEmail: string) =>
      api.organization(organizationId).removeUser({ email: uEmail }),
    onSuccess: () => {
      toast.show('Removed user');
      return queryClient.invalidateQueries({
        queryKey: ['organization', `${organizationId}`],
      });
    },
    onError: () => toast.show('Failed to remove user'),
  });
}

function useOrganizationRemoveStore(organizationId?: number) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (storeId: number) =>
      api.organization(organizationId).removeStore({ storeId }),
    onSuccess: () => {
      toast.show('Removed store');
      return queryClient.invalidateQueries({
        queryKey: ['organization', `${organizationId}`],
      });
    },
    onError: () => toast.show('Failed to remove store'),
  });
}

function AddStoreForm({
  organizationId,
  show,
  onClose,
}: {
  organizationId: number;
  show: boolean;
  onClose: () => void;
}) {
  const { handleSubmit, reset, control } = useForm({
    resolver: zodResolver(addStoreSchema),
  });

  const [debouncedSearch, setDebouncedSearch] = useDebounceValue('', 250);
  const { mutate: addStore } = useOrganizationAddStore(organizationId);

  const storeData = useQuery({
    queryKey: ['store-search', debouncedSearch],
    queryFn: () =>
      api.stores.list({
        searchTerm: debouncedSearch,
      }),
    enabled: debouncedSearch.length > 0,
  });

  const selectOptions =
    storeData.data?.map((store) => ({
      label: `${store.name} (ID:${store.id})`,
      value: `${store.id}`,
      keywords: [store.name, `${store.id}`, store.myShopifyDomain],
    })) ?? [];

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => {
      addStore(values);
      reset();
      onClose();
    })(event).catch(console.error);
  };

  return (
    <Modal show={show} onClose={onClose} title="Add Store">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Controller
          control={control}
          name="storeId"
          render={({ field: { onChange, value } }) => (
            <Combobox
              label="Select Store"
              placeholder="Search stores by name, id or domain"
              options={selectOptions}
              value={value ? `${value}` : undefined}
              onChange={(v) => onChange(v ? Number.parseInt(v, 10) : undefined)}
              onSearchChange={setDebouncedSearch}
            />
          )}
        />

        <Action variant="primary" type="submit">
          Add
        </Action>
      </form>
    </Modal>
  );
}

function AddUserForm({
  organizationId,
  show,
  onClose,
  isCorsoOrg,
}: {
  organizationId: number;
  show: boolean;
  onClose: () => void;
  isCorsoOrg?: boolean;
}) {
  const modifiedUserSchema = isCorsoOrg
    ? addUserSchema.extend({
        email: addUserSchema.shape.email.refine(
          (val) => val.toLowerCase().endsWith('@corso.com'),
          'Only Corso emails can be added to this organization',
        ),
      })
    : addUserSchema.extend({
        email: addUserSchema.shape.email.refine(
          (val) => !val.toLowerCase().endsWith('@corso.com'),
          'Corso emails must be added under the Corso organization',
        ),
      });

  const {
    formState: { errors },
    handleSubmit,
    reset,
    register,
    control,
  } = useForm({
    resolver: zodResolver(modifiedUserSchema),
  });

  const roleOptions = isCorsoOrg
    ? roleCodeOptions.filter((option) => option.value !== RoleCode.admin)
    : roleCodeOptions.filter((option) => option.value !== RoleCode.corsoAdmin);

  const { mutate: addUser } = useOrganizationAddUser(organizationId);

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => {
      addUser(values);
      reset();
      onClose();
    })(event).catch(console.error);
  };

  return (
    <Modal show={show} onClose={onClose} title="Add User">
      {isCorsoOrg && (
        <Alert variant="critical" heading="Corso Admin Access">
          Adding a user to this organization will grant access to all stores.
        </Alert>
      )}
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="userEmail"
          label="Email"
          required
          {...register('email')}
          error={errors.email?.message}
        />
        <TextInput
          id="firstName"
          label="First Name"
          {...register('firstName')}
          error={errors.firstName?.message}
        />
        <TextInput
          id="lastName"
          label="Last Name"
          {...register('lastName')}
          error={errors.lastName?.message}
        />
        <Controller
          control={control}
          name="roleCode"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Role"
              required
              options={roleOptions}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <Action variant="primary" type="submit">
          Add
        </Action>
      </form>
    </Modal>
  );
}

function OrganizationStores({
  organizationId,
  isCorsoOrg,
  onAddStoreClick,
}: {
  organizationId: number;
  isCorsoOrg: boolean;
  onAddStoreClick: () => void;
}) {
  const navigate = useNavigate();

  const [storeToRemove, setStoreToRemove] = useState<Store | null>(null);

  const { mutate: removeStore, isPending: removeStorePending } =
    useOrganizationRemoveStore(organizationId);

  const storeQuery = useQuery({
    queryKey: ['organization', `${organizationId}`, 'stores'],
    queryFn: () => api.organization(organizationId).listStores(),
    enabled: !!organizationId,
  });

  const storeColumns: ColumnDef<Store>[] = [
    idColumn<Store>(),
    nameColumn<Store>(),
    shopifyDomainColumn<Store>(),
    createdOnColumn<Store>(),
    {
      id: 'actions',
      header: '',
      meta: { align: 'right' },
      cell: ({ row }) =>
        isCorsoOrg ? null : (
          <Action
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              setStoreToRemove(row.original);
            }}
            accessibilityLabel="Remove Store From Organization"
            icon="remove"
          />
        ),
    },
  ];

  return (
    <>
      <Card>
        <Card.Line>
          <Card.Heading as="h2">Stores</Card.Heading>
          <Action
            variant="ghost"
            onClick={onAddStoreClick}
            icon="add"
            accessibilityLabel="Add Store"
            disabled={isCorsoOrg}
          />
        </Card.Line>
        {match(storeQuery)
          .with({ isPending: true }, () => (
            <PageStatus.Loading title="Loading stores" />
          ))
          .with({ isError: true }, () => (
            <PageStatus.Error
              title="Failed to load stores"
              error={storeQuery.error}
            />
          ))
          .with({ isSuccess: true, data: P.select() }, (stores) =>
            stores.length > 0 ? (
              <DataTable
                data={stores}
                columns={storeColumns}
                onRowClick={(store) =>
                  navigate({
                    pathname: `/stores/${store.id}/info`,
                    search: new URLSearchParams({
                      returnTo: `/organizations/${organizationId}`,
                    }).toString(),
                  })
                }
              />
            ) : (
              <DataTableStatus
                icon="store"
                title="No stores found"
                details="Add a store to this organization to get started."
              />
            ),
          )
          .otherwise(() => null)}
      </Card>

      <ConfirmModal
        show={storeToRemove !== null}
        title="Remove store"
        prompt={
          storeToRemove ? (
            <>
              Remove <strong>{storeToRemove.name}</strong> from this
              organization?
            </>
          ) : null
        }
        confirmText="Remove"
        variant="destructive"
        loading={removeStorePending}
        onCancel={() => setStoreToRemove(null)}
        onConfirm={() => {
          if (!storeToRemove) return;
          removeStore(storeToRemove.id, {
            onSuccess: () => setStoreToRemove(null),
          });
        }}
      />
    </>
  );
}

export default function OrganizationPage() {
  const { organizationId } = usePathParams(
    z.object({ organizationId: z.coerce.number().optional() }),
  );

  const [showAddUser, setShowAddUser] = useState(false);
  const [showAddStore, setShowAddStore] = useState(false);
  const [userToRemove, setUserToRemove] = useState<User | null>(null);

  const query = useQuery({
    queryKey: ['organization', `${organizationId}`],
    queryFn: () => api.organization(organizationId).get(),
    enabled: !!organizationId,
  });

  const { mutate: removeUser, isPending: removeUserPending } =
    useOrganizationRemoveUser(organizationId);

  return (
    <Page
      size="fullWidth"
      title="Organization"
      backAction={{
        to: '/organizations',
        accessibilityLabel: 'Back to organizations',
      }}
    >
      {match(query)
        .with({ status: 'pending' }, () => (
          <PageStatus.Loading title="Loading organization" />
        ))
        .with({ status: 'error' }, () => (
          <PageStatus.Error
            title="Couldn’t load organization"
            error={query.error}
          />
        ))
        .with({ status: 'success', data: P.select() }, (org) => {
          const isCorsoOrg = org.name === CORSO_STORE_ORGANIZATION_NAME;

          const userColumns: ColumnDef<User>[] = [
            idColumn<User>(),
            { accessorKey: 'email', header: 'Email', enableSorting: true },
            { accessorKey: 'firstName', header: 'First Name' },
            { accessorKey: 'lastName', header: 'Last Name' },
            {
              accessorKey: 'roleCode',
              header: 'Role',
              cell: ({ row }) => (
                <Badge>{roleCodeLabel[row.original.roleCode]}</Badge>
              ),
            },
            createdOnColumn<User>(),
            {
              id: 'actions',
              header: '',
              meta: { align: 'right' },
              cell: ({ row }) => (
                <Action
                  variant="ghost"
                  onClick={() => setUserToRemove(row.original)}
                  accessibilityLabel="Remove User From Organization"
                  icon="remove"
                />
              ),
            },
          ];

          const dataElements = [
            {
              label: 'Name',
              value: org.name,
            },
            {
              label: 'ID',
              value: `${org.id}`,
              copyEnabled: true,
            },
            {
              label: 'Created On',
              value: org.createdOn ? (
                <RelativeDateTime dateTime={org.createdOn} />
              ) : (
                '—'
              ),
            },
          ] satisfies DataListEntry[];

          return (
            <>
              <div className="flex flex-col gap-4">
                <DataListCard items={dataElements} />
                <Card>
                  <Card.Line>
                    <Card.Heading as="h2">Users</Card.Heading>

                    <Action
                      variant="ghost"
                      onClick={() => setShowAddUser(true)}
                      icon="add"
                      accessibilityLabel="Add User"
                    />
                  </Card.Line>

                  {org.users.length > 0 ? (
                    <DataTable data={org.users} columns={userColumns} />
                  ) : (
                    <DataTableStatus
                      icon="users"
                      title="No users found"
                      details="Add a user to this organization to get started."
                    />
                  )}
                </Card>

                <OrganizationStores
                  organizationId={org.id}
                  isCorsoOrg={isCorsoOrg}
                  onAddStoreClick={() => setShowAddStore(true)}
                />
              </div>

              <AddUserForm
                organizationId={org.id}
                show={showAddUser}
                onClose={() => setShowAddUser(false)}
                isCorsoOrg={isCorsoOrg}
              />

              <AddStoreForm
                organizationId={org.id}
                show={showAddStore}
                onClose={() => setShowAddStore(false)}
              />

              <ConfirmModal
                show={userToRemove !== null}
                title="Remove user"
                prompt={
                  userToRemove && (
                    <>
                      Remove <strong>{userToRemove.email}</strong> from{' '}
                      <strong>{org.name}</strong>? They will lose access
                      immediately.
                    </>
                  )
                }
                confirmText="Remove"
                variant="destructive"
                loading={removeUserPending}
                onCancel={() => setUserToRemove(null)}
                onConfirm={() => {
                  if (!userToRemove) return;
                  removeUser(userToRemove.email, {
                    onSuccess: () => setUserToRemove(null),
                  });
                }}
              />
            </>
          );
        })
        .otherwise(() => null)}
    </Page>
  );
}
